import { Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Building2,
  CircleDot,
  Radar,
  Sparkles,
} from "lucide-react";
import { FEATURED } from "@/constants/testIds";
import { ScoreBadge } from "./ScoreBadge";
import { StatusBadge } from "@/components/StatusBadge";

export const FeaturedOpportunity = ({ opportunity }) => {
  if (!opportunity) return null;
  const meta = opportunity.meta ?? {};

  return (
    <section
      id="featured"
      data-testid={FEATURED.root}
      className="mx-auto max-w-7xl px-4 pt-16 pb-8 sm:px-6 lg:px-8 lg:pt-20"
    >
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="fursa-eyebrow inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-intel-800" strokeWidth={2} aria-hidden />
            فرصة اليوم · Cover Story
          </p>
          <h2 className="fursa-section-title mt-3 font-semibold tracking-tight text-ink-900 arabic-headline text-balance">
            أعلى فرصة بحسب الأثر المتوقّع
          </h2>
        </div>
        <div className="fursa-rule w-24" aria-hidden />
      </div>

      <article className="group relative overflow-hidden rounded-2xl border border-surface-200 bg-white shadow-card transition-shadow duration-200 hover:shadow-card-hover">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Briefing panel — solid navy + hairline dot noise. No gradient. */}
          <div className="relative col-span-1 border-b border-intel-800 intel-briefing-bg p-8 text-white lg:col-span-2 lg:border-b-0 lg:border-s lg:border-intel-800">
            <div className="relative flex h-full flex-col justify-between gap-8">
              <div>
                <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-fursa-200">
                  <Radar className="h-3 w-3" strokeWidth={2} aria-hidden />
                  نقاط الفرصة
                </p>
                <div
                  dir="ltr"
                  className="mt-4 flex items-baseline justify-start gap-2 font-mono tabular-nums"
                >
                  <span
                    data-testid={FEATURED.score}
                    className="text-[64px] font-bold leading-none tracking-tightest text-white sm:text-7xl"
                  >
                    {opportunity.score}
                  </span>
                  <span className="text-xl font-medium text-intel-200/80">/ 100</span>
                </div>
                <div className="mt-5">
                  <StatusBadge
                    opportunity={opportunity}
                    meta={meta}
                    variant="solid"
                    size="sm"
                  />
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-y-4 gap-x-6 border-t border-white/10 pt-6 text-sm">
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-widest text-intel-200/70">النوع</dt>
                  <dd className="mt-1.5 font-medium text-white">{opportunity.type}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-widest text-intel-200/70">الدولة</dt>
                  <dd className="mt-1.5 font-medium text-white">{opportunity.country}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-widest text-intel-200/70">القطاع</dt>
                  <dd className="mt-1.5 font-medium text-white">{opportunity.industry}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-widest text-intel-200/70">النشر</dt>
                  <dd className="mt-1.5 font-medium text-white">{opportunity.publishedAt}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Editorial body */}
          <div className="col-span-1 p-6 sm:p-8 lg:col-span-3 lg:p-12">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-200 bg-surface-50 px-2.5 py-1 text-[11px] font-medium text-ink-700">
                <CircleDot className="h-3 w-3" strokeWidth={2} />
                {opportunity.type}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-200 bg-surface-50 px-2.5 py-1 text-[11px] font-medium text-ink-700">
                <MapPin className="h-3 w-3" strokeWidth={1.75} />
                {opportunity.country}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-200 bg-surface-50 px-2.5 py-1 text-[11px] font-medium text-ink-700">
                <Building2 className="h-3 w-3" strokeWidth={1.75} />
                {opportunity.industry}
              </span>
              <ScoreBadge score={opportunity.score} size="sm" />
            </div>

            <h3
              data-testid={FEATURED.title}
              className="mt-6 text-2xl font-bold leading-snug tracking-tighter text-ink-900 sm:text-[28px] lg:text-[32px]"
            >
              {opportunity.title}
            </h3>

            <p className="mt-4 text-base leading-relaxed text-ink-500 lg:text-lg">
              {opportunity.summary}
            </p>

            {opportunity.highlights?.length > 0 && (
              <ul className="mt-6 space-y-2.5 border-t border-surface-100 pt-6">
                {opportunity.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-ink-700">
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-fursa-600"
                      aria-hidden
                    />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-surface-100 pt-6">
              <Link
                to={`/opportunities/${opportunity.id}`}
                data-testid={FEATURED.readMore}
                className="inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800 hover:shadow-md"
              >
                افتح الملف الكامل
                <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
              </Link>
              <span className="text-xs text-ink-500">{opportunity.source}</span>
            </div>
          </div>
        </div>
      </article>
    </section>
  );
};
